/**
 * Sistem Ayarları (BOSS).
 * Şu an sadece puantaj tutarları. İleride diğer ayarlar eklenir.
 */

import { useEffect, useState } from 'react'
import { Settings, Award, Save, CheckCircle2, Send, Lock, Mail, AtSign } from 'lucide-react'
import { doc, getDoc, updateDoc, setDoc, deleteDoc } from 'firebase/firestore'
import {
  getAuth, EmailAuthProvider,
  reauthenticateWithCredential, updatePassword,
} from 'firebase/auth'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { getFirestoreDb } from '@/lib/firebase'
import { useAuth } from '@/contexts/AuthContext'
import {
  ayarlariGetir,
  ayarlariGuncelle,
  VARSAYILAN_AYARLAR,
  type Ayarlar,
} from '@/lib/services/ayarlar-service'

export function AyarlarPage() {
  const { kullanici } = useAuth()
  const [ayarlar, setAyarlar] = useState<Ayarlar>(VARSAYILAN_AYARLAR)
  const [bossTutar, setBossTutar] = useState<string>('')
  const [sorumluTutar, setSorumluTutar] = useState<string>('')
  const [yukleniyor, setYukleniyor] = useState(true)
  const [kaydediliyor, setKaydediliyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)
  const [kaydedildi, setKaydedildi] = useState(false)

  // Telegram chat ID
  const [telegramId, setTelegramId] = useState<string>('')
  const [telegramKayitliId, setTelegramKayitliId] = useState<string>('')
  const [telegramKaydediliyor, setTelegramKaydediliyor] = useState(false)
  const [telegramKaydedildi, setTelegramKaydedildi] = useState(false)
  const [telegramHata, setTelegramHata] = useState<string | null>(null)

  // Şifre değiştir
  const [eskiSifre, setEskiSifre] = useState('')
  const [yeniSifre, setYeniSifre] = useState('')
  const [yeniSifreTekrar, setYeniSifreTekrar] = useState('')
  const [sifreKaydediliyor, setSifreKaydediliyor] = useState(false)
  const [sifreKaydedildi, setSifreKaydedildi] = useState(false)
  const [sifreHata, setSifreHata] = useState<string | null>(null)

  // E-posta değiştir (BOSS)
  const [mevcutEmail, setMevcutEmail] = useState('')
  const [yeniEmail, setYeniEmail] = useState('')
  const [emailKaydediliyor, setEmailKaydediliyor] = useState(false)
  const [emailKaydedildi, setEmailKaydedildi] = useState(false)
  const [emailHata, setEmailHata] = useState<string | null>(null)

  // Giriş kısa adı (login alias)
  const [kisaAd, setKisaAd] = useState('')
  const [kayitliKisaAd, setKayitliKisaAd] = useState('')
  const [kisaAdKaydediliyor, setKisaAdKaydediliyor] = useState(false)
  const [kisaAdKaydedildi, setKisaAdKaydedildi] = useState(false)
  const [kisaAdHata, setKisaAdHata] = useState<string | null>(null)

  async function yukle() {
    try {
      setYukleniyor(true)
      const a = await ayarlariGetir()
      setAyarlar(a)
      setBossTutar(a.puantajBossTutari.toString())
      setSorumluTutar(a.puantajSorumluTutari.toString())

      // BOSS'un kendi telegram chat ID'si
      if (kullanici) {
        const db = getFirestoreDb()
        const snap = await getDoc(doc(db, 'kullanicilar', kullanici.id))
        const cid = (snap.data()?.telegramChatId ?? '') as string
        setTelegramId(cid)
        setTelegramKayitliId(cid)
      }

      // BOSS Auth email (gerçek e-posta)
      const auth = getAuth()
      const email = auth.currentUser?.email ?? ''
      setMevcutEmail(email)
      setYeniEmail(email)

      // Kayıtlı giriş kısa adı
      if (kullanici) {
        const db = getFirestoreDb()
        const ksnap = await getDoc(doc(db, 'kullanicilar', kullanici.id))
        const ka = (ksnap.data()?.kullaniciAdi ?? '') as string
        setKisaAd(ka)
        setKayitliKisaAd(ka)
      }
    } catch (err) {
      console.error(err)
      setHata('Ayarlar yüklenemedi.')
    } finally {
      setYukleniyor(false)
    }
  }

  useEffect(() => {
    yukle()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kullanici?.id])

  async function sifreDegistir() {
    setSifreHata(null)
    setSifreKaydedildi(false)

    if (!eskiSifre || !yeniSifre || !yeniSifreTekrar) {
      setSifreHata('Tüm alanları doldur.')
      return
    }
    if (yeniSifre.length < 6) {
      setSifreHata('Yeni şifre en az 6 karakter olmalı.')
      return
    }
    if (yeniSifre !== yeniSifreTekrar) {
      setSifreHata('Yeni şifreler eşleşmiyor.')
      return
    }

    const auth = getAuth()
    const user = auth.currentUser
    if (!user || !user.email) {
      setSifreHata('Oturum bulunamadı. Lütfen yeniden giriş yap.')
      return
    }

    setSifreKaydediliyor(true)
    try {
      const cred = EmailAuthProvider.credential(user.email, eskiSifre)
      await reauthenticateWithCredential(user, cred)
      await updatePassword(user, yeniSifre)
      setSifreKaydedildi(true)
      setEskiSifre('')
      setYeniSifre('')
      setYeniSifreTekrar('')
      setTimeout(() => setSifreKaydedildi(false), 4000)
    } catch (err) {
      const kod = (err as { code?: string }).code
      if (kod === 'auth/wrong-password' || kod === 'auth/invalid-credential') {
        setSifreHata('Eski şifre yanlış.')
      } else if (kod === 'auth/weak-password') {
        setSifreHata('Yeni şifre çok zayıf (en az 6 karakter).')
      } else if (kod === 'auth/too-many-requests') {
        setSifreHata('Çok fazla deneme yaptın, biraz bekle.')
      } else {
        setSifreHata('Şifre değiştirilemedi: ' + ((err as Error).message ?? kod))
      }
    } finally {
      setSifreKaydediliyor(false)
    }
  }

  async function kisaAdKaydet() {
    if (!kullanici) return
    setKisaAdHata(null)
    setKisaAdKaydedildi(false)
    const temiz = kisaAd.trim().toLowerCase()
    if (!temiz) {
      setKisaAdHata('Kısa ad boş olamaz.')
      return
    }
    if (!/^[a-z0-9._-]+$/.test(temiz)) {
      setKisaAdHata('Sadece harf, rakam, nokta, tire kullan (boşluk ve @ yok).')
      return
    }
    const auth = getAuth()
    const email = auth.currentUser?.email
    if (!email) {
      setKisaAdHata('Oturum e-postası bulunamadı.')
      return
    }
    setKisaAdKaydediliyor(true)
    try {
      const db = getFirestoreDb()
      // Yeni alias yaz
      await setDoc(doc(db, 'loginAliases', temiz), {
        email,
        uid: kullanici.id,
      })
      // Eski alias farklıysa sil
      if (kayitliKisaAd && kayitliKisaAd !== temiz) {
        await deleteDoc(doc(db, 'loginAliases', kayitliKisaAd)).catch(() => {})
      }
      // Kullanıcı dokümanına da yaz
      await updateDoc(doc(db, 'kullanicilar', kullanici.id), { kullaniciAdi: temiz })
      setKayitliKisaAd(temiz)
      setKisaAdKaydedildi(true)
      setTimeout(() => setKisaAdKaydedildi(false), 4000)
    } catch (err) {
      setKisaAdHata('Kaydedilemedi: ' + (err as Error).message)
    } finally {
      setKisaAdKaydediliyor(false)
    }
  }

  async function emailDegistir() {
    setEmailHata(null)
    setEmailKaydedildi(false)
    const temiz = yeniEmail.trim().toLowerCase()
    if (!temiz || !temiz.includes('@')) {
      setEmailHata('Geçerli bir e-posta gir.')
      return
    }
    if (temiz === mevcutEmail.toLowerCase()) {
      setEmailHata('Yeni e-posta mevcut ile aynı.')
      return
    }
    setEmailKaydediliyor(true)
    try {
      const fn = httpsCallable<{ yeniEmail: string }, { ok: boolean; yeniEmail?: string }>(
        getFunctions(undefined, 'europe-west3'),
        'bossEmailGuncelle'
      )
      const res = await fn({ yeniEmail: temiz })
      if (res.data?.ok) {
        setMevcutEmail(res.data.yeniEmail ?? temiz)
        setEmailKaydedildi(true)
        setTimeout(() => setEmailKaydedildi(false), 5000)
      } else {
        setEmailHata('Güncellenemedi.')
      }
    } catch (err) {
      const msg = (err as { message?: string }).message ?? 'Hata'
      if (msg.includes('email-already-exists')) {
        setEmailHata('Bu e-posta başka bir hesapta kullanılıyor.')
      } else {
        setEmailHata(msg)
      }
    } finally {
      setEmailKaydediliyor(false)
    }
  }

  async function telegramKaydet() {
    if (!kullanici) return
    setTelegramHata(null)
    setTelegramKaydedildi(false)
    const temiz = telegramId.trim()
    if (temiz && !/^-?\d+$/.test(temiz)) {
      setTelegramHata('Chat ID sadece rakamlardan oluşmalı.')
      return
    }
    setTelegramKaydediliyor(true)
    try {
      const db = getFirestoreDb()
      await updateDoc(doc(db, 'kullanicilar', kullanici.id), {
        telegramChatId: temiz || null,
      })
      setTelegramKayitliId(temiz)
      setTelegramKaydedildi(true)
      setTimeout(() => setTelegramKaydedildi(false), 3000)
    } catch (err) {
      setTelegramHata('Kaydedilemedi: ' + (err as Error).message)
    } finally {
      setTelegramKaydediliyor(false)
    }
  }

  async function kaydet(ev: React.FormEvent) {
    ev.preventDefault()
    setHata(null)
    setKaydedildi(false)

    const bSayi = parseFloat(bossTutar)
    const sSayi = parseFloat(sorumluTutar)
    if (isNaN(bSayi) || bSayi <= 0) {
      setHata('Boss tutarı pozitif bir sayı olmalı.')
      return
    }
    if (isNaN(sSayi) || sSayi <= 0) {
      setHata('Sorumlu tutarı pozitif bir sayı olmalı.')
      return
    }

    setKaydediliyor(true)
    try {
      await ayarlariGuncelle({
        puantajBossTutari: bSayi,
        puantajSorumluTutari: sSayi,
      })
      setAyarlar({ puantajBossTutari: bSayi, puantajSorumluTutari: sSayi })
      setKaydedildi(true)
      setTimeout(() => setKaydedildi(false), 3000)
    } catch (err) {
      console.error(err)
      setHata('Kaydedilemedi: ' + (err as Error).message)
    } finally {
      setKaydediliyor(false)
    }
  }

  const degisti =
    parseFloat(bossTutar) !== ayarlar.puantajBossTutari ||
    parseFloat(sorumluTutar) !== ayarlar.puantajSorumluTutari

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <Settings className="w-6 h-6 text-gray-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ayarlar</h1>
          <p className="text-sm text-gray-600 mt-0.5">Sistem genelinde uygulanan değerler</p>
        </div>
      </div>

      {yukleniyor ? (
        <div className="card text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      ) : (
        <form onSubmit={kaydet} className="space-y-6 max-w-2xl">
          {/* Puantaj */}
          <div className="card">
            <div className="flex items-center gap-2 mb-4">
              <Award className="w-5 h-5 text-amber-600" />
              <h2 className="font-bold text-gray-900">Puantaj Tutarları</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Boss veya Sorumlu personele +/− puantaj verirken bu tutarlar otomatik
              kullanılır. Personel ekranında tutar seçimi yapılmaz, sistem arka planda
              bu değerleri uygular.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Boss Puantaj Tutarı (₺)
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={bossTutar}
                  onChange={(e) => setBossTutar(e.target.value)}
                  className="input-base text-lg font-semibold max-w-xs"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Boss bir personele "Ödül" verince <strong>+{parseFloat(bossTutar) || 0} ₺</strong>,
                  "Ceza" verince <strong>−{parseFloat(bossTutar) || 0} ₺</strong> uygulanır.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Sorumlu Puantaj Tutarı (₺)
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={sorumluTutar}
                  onChange={(e) => setSorumluTutar(e.target.value)}
                  className="input-base text-lg font-semibold max-w-xs"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Sorumlu sadece bu tutar üzerinden +/− verir. Tutarı göremez.
                  Boss tarafından buradan ayarlanır.
                </p>
              </div>
            </div>
          </div>

          {/* Telegram */}
          <div className="card">
            <div className="flex items-center gap-2 mb-4">
              <Send className="w-5 h-5 text-sky-600" />
              <h2 className="font-bold text-gray-900">Telegram Bildirimleri</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Telegram chat ID'ni gir, kritik bildirimleri telefonuna yollayalım.
              ID'ni almak için Telegram'da <strong>@userinfobot</strong>'a Start bas,
              sana yazdığı sayıyı buraya yapıştır.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Telegram Chat ID
              </label>
              <div className="flex gap-2 max-w-md">
                <input
                  type="text"
                  inputMode="numeric"
                  value={telegramId}
                  onChange={(e) => setTelegramId(e.target.value)}
                  placeholder="123456789"
                  className="input-base font-mono"
                />
                <button
                  type="button"
                  onClick={telegramKaydet}
                  disabled={telegramKaydediliyor || telegramId.trim() === telegramKayitliId}
                  className="btn-primary inline-flex items-center gap-1.5 px-4 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {telegramKaydediliyor ? '...' : 'Kaydet'}
                </button>
              </div>
              {telegramKayitliId && (
                <p className="text-xs text-green-700 mt-1">
                  ✓ Telegram bağlı: <span className="font-mono">{telegramKayitliId}</span>
                </p>
              )}
              {telegramHata && (
                <p className="text-xs text-red-700 mt-1">{telegramHata}</p>
              )}
              {telegramKaydedildi && (
                <p className="text-xs text-green-700 mt-1 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Kaydedildi.
                </p>
              )}
            </div>
          </div>

          {/* Giriş Kısa Adı */}
          <div className="card">
            <div className="flex items-center gap-2 mb-4">
              <AtSign className="w-5 h-5 text-violet-600" />
              <h2 className="font-bold text-gray-900">Giriş Kısa Adı</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Uzun e-posta yerine giriş ekranında bu kısa adı yazarak girebilirsin
              (örn. <strong>boss</strong>). Şifre sıfırlama yine gerçek e-postana gider.
            </p>

            <div className="space-y-3 max-w-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Kısa Ad
                </label>
                <input
                  type="text"
                  value={kisaAd}
                  onChange={(e) => setKisaAd(e.target.value.toLowerCase())}
                  className="input-base font-mono"
                  placeholder="boss"
                />
              </div>
              <div>
                <button
                  type="button"
                  onClick={kisaAdKaydet}
                  disabled={kisaAdKaydediliyor || kisaAd.trim().toLowerCase() === kayitliKisaAd}
                  className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {kisaAdKaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
              {kayitliKisaAd && (
                <p className="text-xs text-green-700">
                  ✓ Giriş için <span className="font-mono font-semibold">{kayitliKisaAd}</span> yazabilirsin.
                </p>
              )}
              {kisaAdHata && <p className="text-xs text-red-700">{kisaAdHata}</p>}
              {kisaAdKaydedildi && (
                <p className="text-xs text-green-700 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Kaydedildi.
                </p>
              )}
            </div>
          </div>

          {/* E-posta Değiştir */}
          <div className="card">
            <div className="flex items-center gap-2 mb-4">
              <Mail className="w-5 h-5 text-emerald-600" />
              <h2 className="font-bold text-gray-900">E-posta Değiştir</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Giriş için kullandığın e-posta adresi. Şifre sıfırlama maili buraya
              gönderilir; gerçekten erişebileceğin bir adres kullan.
            </p>

            <div className="space-y-3 max-w-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Mevcut E-posta
                </label>
                <input
                  type="email"
                  value={mevcutEmail}
                  readOnly
                  className="input-base bg-gray-50 text-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Yeni E-posta
                </label>
                <input
                  type="email"
                  value={yeniEmail}
                  onChange={(e) => setYeniEmail(e.target.value)}
                  className="input-base"
                  placeholder="ornek@gmail.com"
                />
              </div>
              <div>
                <button
                  type="button"
                  onClick={emailDegistir}
                  disabled={emailKaydediliyor || yeniEmail.trim().toLowerCase() === mevcutEmail.toLowerCase()}
                  className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Mail className="w-4 h-4" />
                  {emailKaydediliyor ? 'Güncelleniyor...' : 'E-postayı Güncelle'}
                </button>
              </div>
              {emailHata && (
                <p className="text-xs text-red-700">{emailHata}</p>
              )}
              {emailKaydedildi && (
                <p className="text-xs text-green-700 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> E-postan güncellendi. Sonraki girişte yeni e-postanı kullan.
                </p>
              )}
            </div>
          </div>

          {/* Şifre Değiştir */}
          <div className="card">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-5 h-5 text-rose-600" />
              <h2 className="font-bold text-gray-900">Şifre Değiştir</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Hesabının giriş şifresini değiştir. Güvenlik için önce mevcut şifreni
              gireceksin, sonra yeni şifreni iki kere yazacaksın.
            </p>

            <div className="space-y-3 max-w-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Mevcut Şifre
                </label>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={eskiSifre}
                  onChange={(e) => setEskiSifre(e.target.value)}
                  className="input-base"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Yeni Şifre
                </label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={yeniSifre}
                  onChange={(e) => setYeniSifre(e.target.value)}
                  className="input-base"
                  placeholder="En az 6 karakter"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Yeni Şifre (Tekrar)
                </label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={yeniSifreTekrar}
                  onChange={(e) => setYeniSifreTekrar(e.target.value)}
                  className="input-base"
                />
              </div>
              <div>
                <button
                  type="button"
                  onClick={sifreDegistir}
                  disabled={sifreKaydediliyor}
                  className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Lock className="w-4 h-4" />
                  {sifreKaydediliyor ? 'Değiştiriliyor...' : 'Şifreyi Değiştir'}
                </button>
              </div>
              {sifreHata && (
                <p className="text-xs text-red-700">{sifreHata}</p>
              )}
              {sifreKaydedildi && (
                <p className="text-xs text-green-700 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Şifren güncellendi.
                </p>
              )}
            </div>
          </div>

          {hata && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm">
              {hata}
            </div>
          )}

          {kaydedildi && (
            <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-3 text-sm flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Ayarlar kaydedildi.
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={kaydediliyor || !degisti}
              className="btn-primary flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {kaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
